import React, { createContext, useMemo, useContext, ReactNode, useState, useEffect } from 'react';
import { compile, RuleSet } from './engine';
import { Principal, RbacContextType } from './types/types';
import { getEmptyRuleset } from './rolePacks';
import TokenService from '../services/authentication/token.service';
import RolesService from '../services/roles/roles.service';

const RbacContext = createContext<RbacContextType | null>(null);

interface RbacProviderProps {
    principal?: Principal;
    children: ReactNode;
}

export function RbacProvider({ principal, children }: RbacProviderProps) {
    const [apiRuleSet, setApiRuleSet] = useState<RuleSet | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    // Fetch roles from the API
    useEffect(() => {
        if (principal) return; // Skip API call if principal is provided directly

        const fetchUserRoles = async () => {
            try {
                setLoading(true);
                const rules = await RolesService.getUserRoles();
                setApiRuleSet(rules);
                setError(null);
            } catch (err) {
                console.error('Error fetching roles:', err);
                setError(err instanceof Error ? err : new Error('Failed to fetch roles'));
            } finally {
                setLoading(false);
            }
        };

        // Only fetch if user is authenticated
        const user = TokenService.getUser();
        if (user) {
            fetchUserRoles();
        }
    }, [principal]);

    // Get user from localStorage if no principal provided
    const currentPrincipal = useMemo(() => {
        if (principal) return principal;

        // Try to get user from existing auth system
        const user = TokenService.getUser();
        if (!user) {
            // Return a default anonymous principal with empty ruleset
            return {
                id: 'anonymous',
                orgId: 'default',
                roles: [],
                ruleSets: [getEmptyRuleset()],
            };
        }

        // Extract orgId from user data if available, otherwise use 'default'
        const orgId = user.orgId || user.organizationId || user.customerId || 'default';

        // Extract roles from user data
        const roles = user.roles || [];

        // Use API rules if available, otherwise use empty ruleset
        const ruleSets = apiRuleSet
            ? [apiRuleSet]
            : [getEmptyRuleset()];

        return {
            id: user.id || user.userId || 'unknown',
            orgId,
            roles,
            ruleSets,
            userName: user.userName || user.name,
            email: user.email,
            isAdmin: user.isAdmin || roles.includes('admin'),
        };
    }, [principal, apiRuleSet]);

    // Combine all rule sets into one
    const combinedRuleSet = useMemo<RuleSet>(() => {
        const merged: RuleSet = { allow: [], deny: [] };

        for (const ruleSet of currentPrincipal.ruleSets) {
            merged.allow.push(...ruleSet.allow);
            merged.deny.push(...ruleSet.deny);
        }

        return merged;
    }, [currentPrincipal.ruleSets]);

    // Compile the rules into an evaluation function
    const evalFn = useMemo(() => compile(combinedRuleSet), [combinedRuleSet]);

    // Create the context value
    const value = useMemo<RbacContextType>(() => (
        {
            can: (resource: string, action: string) =>
                evalFn({ orgId: currentPrincipal.orgId })(resource, action),
            principal: currentPrincipal,
            loading,
            error,
            reload: async () => {
                try {
                    setLoading(true);
                    const rules = await RolesService.getUserRoles();
                    setApiRuleSet(rules);
                    setError(null);
                } catch (err) {
                    console.error('Error reloading roles:', err);
                    setError(err instanceof Error ? err : new Error('Failed to reload roles'));
                } finally {
                    setLoading(false);
                }
            }
        }),
        [evalFn, currentPrincipal, loading, error]);

    return (
        <RbacContext.Provider value={value}>
            {children}
        </RbacContext.Provider>
    );
}

export const useRBAC = (): RbacContextType => {
    const context = useContext(RbacContext);
    if (!context) {
        throw new Error('useRBAC must be used within RbacProvider');
    }
    return context;
};

// Export the context for testing purposes
export { RbacContext };
